// src/pages/.../proofs/ProofHeader.jsx
import React from "react";
import { Grid, Typography, Box, Chip, Tooltip } from "@mui/material";
import styles from "../index.module.css";

const DEFAULT_LOGO = "https://elitepromomarketing.com/wp-content/uploads/2024/08/logo-1-600x183.png";

/**
 * Unified ProofHeader
 * - Matches multi-proofs DOM/class structure so the shared CSS applies identically.
 * - Back-compat props, plus optional sticky/compact/rightNode/object-chip support.
 */
export default function ProofHeader({
  title = "Proof Review",
  meta = [],
  rightChips = [],
  logoUrl = DEFAULT_LOGO,
  showLogo = true,
  sticky = false,
  compact = false,
  rightNode = null,
}) {
  const metaLines = (Array.isArray(meta) ? meta : []).filter(Boolean);

  // Normalize chips (strings or {label,color,variant,icon,tooltip})
  const chips = (Array.isArray(rightChips) ? rightChips : []).map((c) =>
    typeof c === "string" ? { label: c, variant: "outlined" } : { variant: "outlined", ...c }
  );

  return (
    <Grid
      container
      className={styles.headerContainer}
      sx={{
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: sticky ? 6 : "auto",
        py: compact ? 1 : undefined, // keep baseline padding from CSS unless compact
        alignItems: "center",
      }}
    >
      {/* Logo — keep same grid item & class as multi-proofs */}
      {showLogo && (
        <Grid item className={styles.headerLogoContainer}>
          <img src={logoUrl} className={styles.headerLogo} alt="Logo" />
        </Grid>
      )}

      {/* Title + meta — same class as multi-proofs */}
      <Grid item className={styles.TitleContainer}>
        <Box sx={{ width: "100%" }}>
          <Typography variant="h4" gutterBottom color="white">
            {title}
          </Typography>

          {/* Multi-proofs renders meta as stacked lines (with <br/>). Mirror that exactly. */}
          {metaLines.length > 0 && (
            <Typography variant="body1" color="white">
              {metaLines.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < metaLines.length - 1 && <><br /></>}
                </span>
              ))}
            </Typography>
          )}
        </Box>
      </Grid>

      {/* Right-side chips / custom right node — same classes as multi-proofs */}
      <Grid item className={`${styles.rightBar} ${styles.rightAbsolute}`}>
        <Box className={styles.rightBarInner}>
          {rightNode
            ? rightNode
            : chips.map((chip, idx) => {
                const chipEl = (
                  <Chip
                    key={idx}
                    size="small"
                    label={chip.label}
                    variant={chip.variant || "outlined"}
                    color={chip.color || "default"}
                    icon={chip.icon || undefined}
                  />
                );
                return chip.tooltip ? (
                  <Tooltip key={idx} arrow title={chip.tooltip}>
                    <span>{chipEl}</span>
                  </Tooltip>
                ) : (
                  chipEl
                );
              })}
        </Box>
      </Grid>
    </Grid>
  );
}
